import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ModeSelector from './ModeSelector';
import UpcSelect from './UpcSelect';
import ToSoInput from './ToSoInput';
import ProductTable from './ProductTable';
import QtyInput from './QtyInput';
import PrinterSelect from './PrinterSelect';
import TemplateSelectShinola from './TemplateSelectShinola';
import {
  getBrandFromSku,
  getSkuCategory,
  getSkuCategoryName,
  shouldShowOrientation,
  getFilsonTemplateByOrientation
} from '../utils/templateMapping';

// Component that contains the main label printer functionality
const LabelPrinterApp = () => {
  const { logout, getUserAttributes, getAccessToken } = useAuth();

  // Mode selection: 'upc' or 'toso'
  const [mode, setMode] = useState('upc');

  // State for API tokens - now using Cognito authentication
  const [cognitoToken, setCognitoToken] = useState(null);

  // Get Cognito token on component mount
  React.useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getAccessToken();
        setCognitoToken(token);
      } catch (error) {
        console.error('Failed to get Cognito token:', error);
      }
    };

    fetchToken();
  }, [getAccessToken]);

  // Get user attributes
  const userAttributes = getUserAttributes();

  const userName = userAttributes['cognito:username']

  // Detect if running in staging environment
  const isStaging = !window.location.href.includes('mixo.bedrockmfg');
  const STAGING_PRINT_LIMIT = 5;
  // UPC Mode state
  const [productData, setProductData] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedShinolaTemplate, setSelectedShinolaTemplate] = useState('');
  const [orientation, setOrientation] = useState(() => {
    const savedOrientation = localStorage.getItem('mixo-printer-orientation');
    return (savedOrientation && (savedOrientation === '0' || savedOrientation === '90' || savedOrientation === '180')) ? savedOrientation : '0';
  });
  const [lastProductUpdate, setLastProductUpdate] = useState(0);
  const [lastStateUpdate, setLastStateUpdate] = useState(Date.now());

  // TO/SO Mode state
  const [tosoProducts, setTosoProducts] = useState([]);
  const [tosoProductStates, setTosoProductStates] = useState({});
  const [tranid, setTranid] = useState('');
  const [transactionType, setTransactionType] = useState(null); // 'TO' or 'SO'
  const [paginationInfo, setPaginationInfo] = useState({
    hasMore: false,
    nextOffset: 0,
    currentOffset: 0,
    totalResults: null,
    currentPage: 1,
    totalPages: null
  });
  const [isLoadingMorePages, setIsLoadingMorePages] = useState(false);

  // Default price settings for TO/SO mode
  const [defaultPriceSettings, setDefaultPriceSettings] = useState({
    selectedCurrency: 'USD',
    selectedPriceType: 'current'
  });

  // Shared state
  const [selectedPrinter, setSelectedPrinter] = useState('');

  // Printer list for TO/SO mode
  const [allPrinters, setAllPrinters] = useState([]);

  // Template selection - add manual template selection
  // const [selectedTemplate, setSelectedTemplate] = useState('');
  // const [useManualTemplate, setUseManualTemplate] = useState(false);

  // Loading state for print jobs
  const [isLoading, setIsLoading] = useState(false);

  // Result modal state
  const [showResultModal, setShowResultModal] = useState(false);
  const [printResult, setPrintResult] = useState(null);

  // New state for metafields modal
  const [showMetafieldsModal, setShowMetafieldsModal] = useState(false);
  const [templateMetafields, setTemplateMetafields] = useState(null);
  const [isLoadingMetafields, setIsLoadingMetafields] = useState(false);

  // Add confirmation modal state for quantity over 100
  const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState(null);

  // Batch print (TO/SO) modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchJobs, setBatchJobs] = useState([]); // [{index, product, state, printerId, template, quantity, status, response}]
  const [isBatchPrinting, setIsBatchPrinting] = useState(false);
  const [batchPrintMode, setBatchPrintMode] = useState('auto'); // 'auto' or 'manual'
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [cancelBatchPrint, setCancelBatchPrint] = useState(false);

  // Save orientation to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('mixo-printer-orientation', orientation);
  }, [orientation]);

  // Auto-scroll to current job in manual mode
  React.useEffect(() => {
    if (batchPrintMode === 'manual' && showBatchModal && currentBatchIndex >= 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const currentJobElement = document.querySelector('.job-card.current-job');
        if (currentJobElement) {
          currentJobElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [currentBatchIndex, batchPrintMode, showBatchModal]);

  // Get brand and category information
  const getBrandInfo = () => {
    if (!productData?.sku) return { brand: null, category: null, categoryName: null };

    const brand = getBrandFromSku(productData.sku);
    const category = getSkuCategory(productData.sku);
    const categoryName = getSkuCategoryName(category);

    return { brand, category, categoryName };
  };

  // Get the final template to use based on brand and selection
  const getFinalTemplate = () => {
    if (!productData?.sku) return null;

    const { brand } = getBrandInfo();

    if (brand === 'Shinola') {
      // For Shinola, use selected template
      return selectedShinolaTemplate;
    } else if (brand === 'Filson') {
      // For Filson, use orientation-based template (existing logic)
      return getFilsonTemplateByOrientation(orientation);
    }

    return null;
  };

  // Handle mode change - reset all state
  const handleModeChange = (newMode) => {
    setMode(newMode);
    // Reset all state
    setProductData(null);
    setTosoProducts([]);
    setTosoProductStates({});
    setTranid('');
    setTransactionType(null);
    setQuantity(1);
    setSelectedShinolaTemplate('');
    setAllPrinters([]); // Clear printer cache
    setSelectedPrinter(''); // Clear selected printer
    setPaginationInfo({
      hasMore: false,
      nextOffset: 0,
      currentOffset: 0,
      totalResults: null,
      currentPage: 1,
      totalPages: null
    });
  };

  const handleProductDataFetched = (data) => {
    setProductData(data);
    setSelectedShinolaTemplate('');
    setLastProductUpdate(Date.now());
    setLastStateUpdate(Date.now());
  };

  const handleToSoDataFetched = async (products, transactionId, txnType, pagination) => {
    setTosoProducts(products);
    setTranid(transactionId);
    setTransactionType(txnType || (transactionId.startsWith('TO') ? 'TO' : 'SO'));

    // Update pagination info with page calculation
    if (pagination) {
      const totalPages = pagination.totalResults ? Math.ceil(pagination.totalResults / 300) : null;
      const currentPage = Math.floor(pagination.currentOffset / 300) + 1;
      setPaginationInfo({
        ...pagination,
        currentPage,
        totalPages
      });
    }

    // Fetch printers once when TO/SO data is loaded
    if (!allPrinters || allPrinters.length === 0) {
      await loadPrintersForToSo();
    }
  };

  // Navigate to a specific page
  const handleNavigateToPage = async (pageNumber) => {
    if (!tranid || isLoadingMorePages || pageNumber === paginationInfo.currentPage) return;

    setIsLoadingMorePages(true);
    try {
      const offset = (pageNumber - 1) * 300;
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const requestBody = {
        tranid: tranid,
        offset: offset
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      if (cognitoToken) {
        headers['Authorization'] = `Bearer ${cognitoToken}`;
      }

      const response = await fetch(`${apiBase}/mixo/so-to`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch page');
      }

      const data = await response.json();

      // Process items like in fetchToSoData
      if (data.items && data.items.length > 0) {
        // Aggregate items by itemid, upccode, and linesequencenumber
        const aggregatedMap = data.items.reduce((map, item) => {
          const key = `${item.itemid ?? ''}|${item.upcode ?? ''}|${item.linesequencenumber ?? ''}`;
          const existing = map.get(key);

          if (existing) {
            if (item.pricelevelname === 'Retail (MSRP)') {
              existing.quantity = Math.abs(Number(item.quantity) || 0);
            }
            if (item.pricelevelname === 'Retail (MSRP)') {
              existing.usd_current_price = item.usd_current_price || item.unitprice;
              existing.cad_current_price = item.cad_current_price;
            }
            if (item.usd_future_price) {
              existing.usd_future_price = item.usd_future_price;
            }
            if (item.cad_future_price) {
              existing.cad_future_price = item.cad_future_price;
            }
            if (item.usd_outlet_price) {
              existing.usd_outlet_price = item.usd_outlet_price;
            }
            if (item.cad_outlet_price) {
              existing.cad_outlet_price = item.cad_outlet_price;
            }
          } else {
            const newItem = { ...item };
            if (item.pricelevelname === 'Retail (MSRP)') {
              newItem.quantity = Math.abs(Number(item.quantity) || 0);
            } else {
              newItem.quantity = 0;
            }
            newItem.usd_current_price = item.usd_current_price || item.unitprice;
            newItem.cad_current_price = item.cad_current_price;
            newItem.usd_future_price = item.usd_future_price;
            newItem.cad_future_price = item.cad_future_price;
            newItem.usd_outlet_price = item.usd_outlet_price;
            newItem.cad_outlet_price = item.cad_outlet_price;
            map.set(key, newItem);
          }
          return map;
        }, new Map());

        const aggregatedItems = Array.from(aggregatedMap.values());
        const filteredItems = aggregatedItems.filter(item => item.quantity > 0);

        // Combine quantities for duplicate SKUs
        const skuMap = new Map();
        filteredItems.forEach(item => {
          const sku = item.upcode || item.itemid;
          if (skuMap.has(sku)) {
            const existing = skuMap.get(sku);
            existing.quantity += item.quantity;
          } else {
            skuMap.set(sku, { ...item });
          }
        });

        const consolidatedItems = Array.from(skuMap.values());

        console.log(`Page ${pageNumber}: Fetched ${data.items.length} raw items, consolidated to ${consolidatedItems.length} unique SKUs`);

        // REPLACE products with new page data (not append)
        setTosoProducts(consolidatedItems);
        setTosoProductStates({}); // Reset product states for new page

        // Update pagination info
        const totalPages = data.totalResults ? Math.ceil(data.totalResults / 300) : null;
        setPaginationInfo({
          hasMore: data.hasMore || false,
          nextOffset: data.nextOffset || 0,
          currentOffset: data.offset || 0,
          totalResults: data.totalResults || null,
          currentPage: pageNumber,
          totalPages
        });
      }
    } catch (error) {
      console.error('Error loading page:', error);
      setPrintResult({
        success: false,
        message: '❌ Failed to load page',
        details: error.message
      });
      setShowResultModal(true);
    } finally {
      setIsLoadingMorePages(false);
    }
  };

  const handleToSoProductsChange = (productStates) => {
    setTosoProductStates(productStates);
  };

  // Handle default price settings change
  const handleDefaultPriceChange = (updates) => {
    setDefaultPriceSettings(prev => ({ ...prev, ...updates }));
  };

  // Apply default price settings to all products
  const applyDefaultPriceToAll = () => {
    // Create updated states with new pricing
    const updatedStates = {};
    let count = 0;

    Object.keys(tosoProductStates).forEach(index => {
      updatedStates[index] = {
        ...tosoProductStates[index],
        selectedCurrency: defaultPriceSettings.selectedCurrency,
        selectedPriceType: defaultPriceSettings.selectedPriceType
      };
      count++;
    });

    // Update the state
    setTosoProductStates(updatedStates);

    // Trigger a re-render by updating a counter
    setLastStateUpdate(Date.now());

    // Show success feedback
    const priceTypeLabel = defaultPriceSettings.selectedPriceType === 'none'
      ? 'No Price'
      : defaultPriceSettings.selectedPriceType === 'line'
        ? 'Line Price'
        : defaultPriceSettings.selectedPriceType === 'outlet'
          ? `${defaultPriceSettings.selectedCurrency} Outlet`
          : `${defaultPriceSettings.selectedCurrency} ${defaultPriceSettings.selectedPriceType === 'current' ? 'Current' : 'Future'}`;

    setPrintResult({
      success: true,
      message: `✅ Applied default pricing to ${count} item${count !== 1 ? 's' : ''}`,
      details: `All items now use: ${priceTypeLabel}`
    });
    setShowResultModal(true);
  };

  // Print a single line in TO/SO mode
  const handlePrintLine = async (index) => {
    if (mode !== 'toso') return;
    const product = tosoProducts[index];
    const state = tosoProductStates[index];

    const effectivePrinter = state?.selectedPrinter || selectedPrinter;
    if (!product || !state || !state.selectedTemplate || !effectivePrinter || (state.quantity || 0) <= 0) {
      setPrintResult({
        success: false,
        message: 'Please select a template and printer (global or per-line) and set quantity > 0 for this item.',
        details: { index, state }
      });
      setShowResultModal(true);
      return;
    }

    // Check staging limit for copies
    if (isStaging && state.quantity > STAGING_PRINT_LIMIT) {
      setPrintResult({
        success: false,
        message: `🚧 Staging Environment Limit`,
        details: `One or more items have a quantity over ${STAGING_PRINT_LIMIT}. Please note that this is a limitation of the staging environment. For production needs, please use the production environment.`
      });
      setShowResultModal(true);
      return;
    }

    setIsLoading(true);
    try {
      const singlePayload = createSingleProductPayload(
        product,
        state.quantity,
        state.selectedTemplate,
        effectivePrinter,
        state
      );
      console.log('single payload');
      console.log(singlePayload)
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';

      const response = await fetch(`${apiBase}/batender-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cognitoToken}`,
        },
        body: JSON.stringify({
          method: 'POST',
          url: 'https://am1.bartendercloud.com/api/actions?Wait=5s',
          payload: singlePayload
        })
      });

      const responseData = await response.text();
      let parsedResponse;
      try { parsedResponse = JSON.parse(responseData); } catch { parsedResponse = responseData; }

      setPrintResult({
        success: response && response.ok,
        message: 'Print job submitted for 1 item',
        details: parsedResponse
      });
      setShowResultModal(true);
    } catch (err) {
      setPrintResult({ success: false, message: `Print job error: ${err.message}`, details: err });
      setShowResultModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Load printers for TO/SO mode
  const loadPrintersForToSo = async () => {
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/batender-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cognitoToken}`,
        },
        body: JSON.stringify({
          method: 'GET',
          url: 'https://am1.bartendercloud.com/api/printers'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAllPrinters(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching printers:', error);
    }
  };

  const handleQuantityChange = (value) => {
    // If quantity is over 100, show confirmation modal
    if (value > 100) {
      setPendingQuantity(value);
      setShowQuantityConfirm(true);
    } else {
      setQuantity(value);
    }
  };

  // Handle confirmation for quantities over 100
  const handleConfirmHighQuantity = () => {
    setQuantity(pendingQuantity);
    setShowQuantityConfirm(false);
    setPendingQuantity(null);
  };

  const handleCancelHighQuantity = () => {
    setShowQuantityConfirm(false);
    setPendingQuantity(null);
  };


  const handlePrinterChange = (event) => {
    setSelectedPrinter(event.target.value);
    setLastStateUpdate(Date.now());
  };

  // Helper to display printer label from id
  const getPrinterLabel = (printerId) => {
    if (!printerId) return '';
    const p = (allPrinters || []).find(x => x.PrinterId === printerId);
    return p ? `${p.Name} (${p.Client.FQDName})` : printerId;
  };

  // Helper to create payload for a single product
  const createSingleProductPayload = (product, qty, template, printer, productState = null) => {
    const currentDate = new Date().toLocaleDateString('en-US');
    const brand = getBrandFromSku(product.itemid || product.sku);
    const isShinolaTemplate = brand === 'Shinola';

    // Calculate the price based on selection (works for both UPC, TO, and SO modes)
    let selectedPrice = product.unitprice || product.retailPrice || '0';
    let selectedCurrency = 'USD';

    // Handle UPC mode with 'none' price type
    if (mode === 'upc' && product.priceType === 'none') {
      selectedPrice = '';
      selectedCurrency = '';
    } else if (mode === 'upc' && product.currency) {
      // Use the selected currency from UPC mode
      selectedCurrency = product.currency;
    }

    if (mode === 'toso' && productState) {
      const priceType = productState.selectedPriceType || 'current';

      // If no price is selected (Filson only), use empty strings
      if (priceType === 'none') {
        selectedPrice = '';
        selectedCurrency = '';
      }
      // If line price is selected, use transaction line price
      else if (priceType === 'line') {
        selectedPrice = product.transaction_line_price || product.usd_current_price || '0';
        selectedCurrency = 'USD'; // Line price doesn't have currency context
      }
      // If outlet price is selected, use outlet prices with currency selection
      else if (priceType === 'outlet') {
        selectedCurrency = productState.selectedCurrency || 'USD';
        if (selectedCurrency === 'USD') {
          selectedPrice = product.usd_outlet_price || '0';
        } else { // CAD
          selectedPrice = product.cad_outlet_price || '0';
        }
      } else {
        // For retail prices, use selected currency and price type
        selectedCurrency = productState.selectedCurrency || 'USD';

        if (selectedCurrency === 'USD') {
          selectedPrice = priceType === 'future'
            ? (product.usd_future_price || product.usd_current_price || '0')
            : (product.usd_current_price || '0');
        } else { // CAD
          selectedPrice = priceType === 'future'
            ? (product.cad_future_price || product.cad_current_price || '0')
            : (product.cad_current_price || '0');
        }
      }
    }

    const namedDataSources = {
      UPC: product.upccode || product.upc || '',
      Style: product.custitem_psgss_style_number || '',
      pattern: product.pattern || '',
      Config: product.configuration || '',
      Description: product.displayname || product.name || '',
      Price: selectedPrice === '' ? '' : `$${selectedPrice}`,
      Size: product.product_size || '',
      Color: product.product_color || '',
      currency: selectedCurrency,
      Name: product.itemid || product.sku || '',
    };

    if (isShinolaTemplate) {
      Object.assign(namedDataSources, {
        futureprice: product.custitem_bmc_future_price ? `$${product.custitem_bmc_future_price}` : '',
        displayname: product.displayname || product.name || '',
        NRFcolor: product.custitem_psgss_nrf_color_code?.toString() || '',
        NRFsize: product.custitem_psgss_nrf_size_code?.toString() || '',
        Brand: product.brand || '',
        Tooling: '',
        batterylifespan: product.battery_expiration_date || '',
        Date: currentDate
      });
    }

    return {
      PrintBTWAction: {
        DocumentFile: template,
        Printer: `printer:${printer}`,
        VerifyPrintJobIsComplete: true,
        Copies: qty.toString(),
        NamedDataSources: namedDataSources,
        QueryPrompts: [
          {
            Name: "Item_Number",
            Value: product.id?.toString() || ''
          }
        ]
      }
    };
  };

  const createPayload = () => {

    // UPC Mode
    if (mode === 'upc') {
      if (!productData) return null;
      if (!selectedPrinter) return null;

      // Validate state
      if (lastProductUpdate > lastStateUpdate) {
        console.warn('Stale state detected');
        return null;
      }

      const brandInfo = getBrandInfo();
      if (brandInfo.brand === 'Shinola' && !selectedShinolaTemplate) {
        console.warn('Shinola product but no template');
        return null;
      }

      const documentFile = getFinalTemplate();
      return createSingleProductPayload(productData, quantity, documentFile, selectedPrinter);
    }

    // TO/SO Mode - return array of payloads
    if (mode === 'toso') {
      if (!tosoProducts || tosoProducts.length === 0) return null;

      const payloads = [];
      tosoProducts.forEach((product, index) => {
        const state = tosoProductStates[index];
        if (!state || !state.enabled || !state.selectedTemplate) return;
        const effectivePrinter = state.selectedPrinter || selectedPrinter;
        if (!effectivePrinter || (state.quantity || 0) <= 0) return;

        const payload = createSingleProductPayload(
          product,
          state.quantity,
          state.selectedTemplate,
          effectivePrinter,
          state
        );
        payloads.push(payload);
      });

      return payloads.length > 0 ? payloads : null;
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Extra safety check: log to help debug accidental submissions
    console.log('🖨️ Print submit triggered');

    const payload = createPayload();
    if (!payload) {
      let errorMessage = 'Print job failed: Invalid data or missing selections.';

      if (mode === 'toso') {
        errorMessage = 'Please ensure each product has a template and printer selected.';
      } else {
        errorMessage = 'Please ensure all required fields are filled.';
      }

      setPrintResult({
        success: false,
        message: errorMessage,
        details: 'Check that all products have templates and printers selected.'
      });
      setShowResultModal(true);
      return;
    }

    // Check staging limit
    const payloads = Array.isArray(payload) ? payload : [payload];

    // In staging, check both number of commands and copies per command
    if (isStaging) {
      // Check if number of separate print commands exceeds limit
      if (payloads.length > STAGING_PRINT_LIMIT) {
        setPrintResult({
          success: false,
          message: `🚧 Staging Environment Limit Exceeded`,
          details: `This is a staging environment and is limited to ${STAGING_PRINT_LIMIT} print commands per run. You attempted to send ${payloads.length} print commands. Please reduce the number of items or use the production environment for larger batches.`
        });
        setShowResultModal(true);
        return;
      }

      // Check if any single command has more than STAGING_PRINT_LIMIT copies
      if (mode === 'upc' && quantity > STAGING_PRINT_LIMIT) {
        setPrintResult({
          success: false,
          message: `🚧 Staging Environment Limit`,
          details: `One or more items have a quantity over ${STAGING_PRINT_LIMIT}. Please note that this is a limitation of the staging environment. For production needs, please use the production environment.`
        });
        setShowResultModal(true);
        return;
      }

      // For TO/SO mode, check each payload's copy count
      if (mode === 'toso') {
        const invalidJob = payloads.find((p, idx) => {
          const copies = parseInt(p.PrintBTWAction?.Copies || '0');
          return copies > STAGING_PRINT_LIMIT;
        });

        if (invalidJob) {
          setPrintResult({
            success: false,
            message: `🚧 Staging Environment Limit`,
            details: `One or more items have a quantity over ${STAGING_PRINT_LIMIT}. Please note that this is a limitation of the staging environment. For production needs, please use the production environment.`
          });
          setShowResultModal(true);
          return;
        }
      }
    }

    setIsLoading(true);

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';

      // Handle single payload (UPC mode) or multiple payloads (TO/SO mode)
      const payloads = Array.isArray(payload) ? payload : [payload];
      console.log(`Sending ${payloads.length} print job(s) to printer`);

      const results = [];
      for (const singlePayload of payloads) {
        console.log('Sending to printer:', singlePayload);

        const response = await fetch(`${apiBase}/batender-requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cognitoToken}`,
          },
          body: JSON.stringify({
            method: 'POST',
            url: 'https://am1.bartendercloud.com/api/actions?Wait=5s',
            payload: singlePayload
          })
        });

        const responseData = await response.text();
        let parsedResponse;

        try {
          parsedResponse = JSON.parse(responseData);
        } catch (e) {
          parsedResponse = responseData;
        }

        results.push({
          success: response.ok,
          status: response.status,
          response: parsedResponse
        });
      }

      // Aggregate results
      const successCount = results.filter(r => r.success).length;
      const totalCount = results.length;

      if (successCount === totalCount) {
        setPrintResult({
          success: true,
          message: `✅ All print jobs successful! (${successCount}/${totalCount})`,
          details: results
        });
      } else if (successCount > 0) {
        setPrintResult({
          success: false,
          message: `⚠️ Partial success: ${successCount}/${totalCount} print jobs completed`,
          details: results
        });
      } else {
        setPrintResult({
          success: false,
          message: `❌ All print jobs failed (0/${totalCount})`,
          details: results
        });
      }

      setShowResultModal(true);

    } catch (error) {
      console.error('Print job error:', error);
      setPrintResult({
        success: false,
        message: `Print job error: ${error.message}`,
        details: error
      });
      setShowResultModal(true);
    } finally {
      setIsLoading(false);
    }
  };



  const handleCloseMetafieldsModal = () => {
    setShowMetafieldsModal(false);
    setTemplateMetafields(null);
    setIsLoadingMetafields(false);
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setPrintResult(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Basic validation check for the submit button
  const isFormValid = () => {
    if (mode === 'upc') {
      if (!productData || quantity < 1) return false;
      if (!selectedPrinter) return false;
      const { brand } = getBrandInfo();
      if (brand === 'Shinola' && !selectedShinolaTemplate) return false;
      return true;
    }

    if (mode === 'toso') {
      if (!tosoProducts || tosoProducts.length === 0) return false;
      // At least one product has template, printer (per-line or global), quantity > 0, and is enabled
      const hasValidProduct = tosoProducts.some((_, index) => {
        const state = tosoProductStates[index];
        const effectivePrinter = state?.selectedPrinter || selectedPrinter;
        return state && state.enabled && state.selectedTemplate && effectivePrinter && state.quantity > 0;
      });
      return hasValidProduct;
    }

    return false;
  };

  // Build batch jobs for TO/SO and open modal
  const openBatchModal = () => {
    if (mode !== 'toso') return;
    if (!tosoProducts || tosoProducts.length === 0) return;
    const jobs = [];
    tosoProducts.forEach((product, index) => {
      const state = tosoProductStates[index];
      if (!state || !state.enabled || !state.selectedTemplate) return;
      const effectivePrinter = state.selectedPrinter || selectedPrinter;
      if (!effectivePrinter || (state.quantity || 0) <= 0) return;

      // In staging environment, cap quantity at STAGING_PRINT_LIMIT
      const cappedQuantity = isStaging && state.quantity > STAGING_PRINT_LIMIT
        ? STAGING_PRINT_LIMIT
        : state.quantity;

      jobs.push({
        index,
        product,
        state,
        printerId: effectivePrinter,
        template: state.selectedTemplate,
        quantity: cappedQuantity,
        status: 'pending',
        response: null
      });
    });

    if (jobs.length === 0) {
      setPrintResult({
        success: false,
        message: 'No valid items to print. Please enable items and select template and printer (global or per-line).',
        details: null
      });
      setShowResultModal(true);
      return;
    }

    setBatchJobs(jobs);
    setCurrentBatchIndex(0);
    setBatchPrintMode('auto');
    setShowBatchModal(true);
  };

  const startBatchPrinting = async () => {
    if (isBatchPrinting) return;

    // Check staging limit for number of commands
    if (isStaging && batchJobs.length > STAGING_PRINT_LIMIT) {
      setPrintResult({
        success: false,
        message: `🚧 Staging Environment Limit Exceeded`,
        details: `This is a staging environment and is limited to ${STAGING_PRINT_LIMIT} print commands per run. You attempted to send ${batchJobs.length} print commands. Please reduce the number of items or use the production environment for larger batches.`
      });
      setShowResultModal(true);
      closeBatchModal();
      return;
    }

    // Check staging limit for copies per command
    if (isStaging) {
      const invalidJob = batchJobs.find(job => job.quantity > STAGING_PRINT_LIMIT);
      if (invalidJob) {
        setPrintResult({
          success: false,
          message: `🚧 Staging Environment Limit`,
          details: `One or more items have a quantity over ${STAGING_PRINT_LIMIT}. Please note that this is a limitation of the staging environment. For production needs, please use the production environment.`
        });
        setShowResultModal(true);
        closeBatchModal();
        return;
      }
    }

    setCancelBatchPrint(false);
    setIsBatchPrinting(true);
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const updated = [...batchJobs];
      console.log(updated)
      for (let i = 0; i < updated.length; i++) {
        // Check if user cancelled
        if (cancelBatchPrint) {
          console.log('Batch print cancelled by user');
          // Mark remaining jobs as cancelled
          for (let j = i; j < updated.length; j++) {
            if (updated[j].status === 'pending') {
              updated[j] = { ...updated[j], status: 'cancelled' };
            }
          }
          setBatchJobs([...updated]);
          break;
        }

        // Mark in-progress
        updated[i] = { ...updated[i], status: 'printing' };
        setBatchJobs([...updated]);

        const job = updated[i];
        const payload = createSingleProductPayload(job.product, job.quantity, job.template, job.printerId, job.state);
        console.log(payload)
        try {
          const response = await fetch(`${apiBase}/batender-requests`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${cognitoToken}`,
            },
            body: JSON.stringify({
              method: 'POST',
              url: 'https://am1.bartendercloud.com/api/actions?Wait=5s',
              payload
            })
          });
          await new Promise(resolve => setTimeout(resolve, 3000));
          console.log(`Printing line ${i + 1} of ${updated.length}`);
          const responseData = await response.text();
          let parsed;
          try { parsed = JSON.parse(responseData); } catch { parsed = responseData; }
          updated[i] = { ...updated[i], status: 'success', response: parsed };
          setBatchJobs([...updated]);
        } catch (err) {
          updated[i] = { ...updated[i], status: 'error', response: err?.message || err };
          setBatchJobs([...updated]);
        }
      }
    } finally {
      setIsBatchPrinting(false);
      setCancelBatchPrint(false);
    }
  };

  const handleCancelBatchPrint = () => {
    setCancelBatchPrint(true);
  };

  // Print single job in manual mode
  const printSingleBatchJob = async (jobIndex) => {
    if (jobIndex >= batchJobs.length) return;

    setIsBatchPrinting(true);
    const updated = [...batchJobs];
    updated[jobIndex] = { ...updated[jobIndex], status: 'printing' };
    setBatchJobs([...updated]);

    const job = updated[jobIndex];
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const payload = createSingleProductPayload(job.product, job.quantity, job.template, job.printerId, job.state);

    try {
      const response = await fetch(`${apiBase}/batender-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cognitoToken}`,
        },
        body: JSON.stringify({
          method: 'POST',
          url: 'https://am1.bartendercloud.com/api/actions?Wait=5s',
          payload
        })
      });

      const responseData = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseData); } catch { parsed = responseData; }

      updated[jobIndex] = { ...updated[jobIndex], status: 'success', response: parsed };
      setBatchJobs([...updated]);
    } catch (err) {
      updated[jobIndex] = { ...updated[jobIndex], status: 'error', response: err?.message || err };
      setBatchJobs([...updated]);
    } finally {
      setIsBatchPrinting(false);
    }
  };

  const handleManualNext = async () => {
    await printSingleBatchJob(currentBatchIndex);
    if (currentBatchIndex < batchJobs.length - 1) {
      setCurrentBatchIndex(currentBatchIndex + 1);
    }
  };

  const handleManualSkip = () => {
    const updated = [...batchJobs];
    updated[currentBatchIndex] = { ...updated[currentBatchIndex], status: 'skipped' };
    setBatchJobs(updated);
    if (currentBatchIndex < batchJobs.length - 1) {
      setCurrentBatchIndex(currentBatchIndex + 1);
    }
  };

  const closeBatchModal = () => {
    if (isBatchPrinting) return;
    setShowBatchModal(false);
    setBatchJobs([]);
  };

  return (
    <div className="App container">
      <style jsx>{`
        .App {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .App-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .title-section {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        }

        .App-header h1 {
          margin: 0;
          font-size: 2.5rem;
          font-weight: 700;
        }

        .staging-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
          border: 2px solid rgba(255, 255, 255, 0.3);
          animation: pulse 2s infinite;
        }

        .staging-text {
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }

        .staging-icon {
          font-size: 1rem;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(255, 107, 53, 0.6);
          }
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .welcome-text {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .logout-button {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .logout-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .label-form {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .form-section {
          background: #ffffff;
          border: 1px solid #e1e5e9;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .form-section:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .form-section h3 {
          margin: 0 0 20px 0;
          color: #2c3e50;
          font-size: 1.3rem;
          font-weight: 600;
          border-bottom: 2px solid #3498db;
          padding-bottom: 8px;
          text-align: left;
        }

        .product-info {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        .product-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .product-details p {
          margin: 0;
          padding: 12px;
          background: white;
          border-radius: 8px;
          border-left: 4px solid #3498db;
          font-size: 0.95rem;
        }

        .brand-highlight {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-left: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .brand-shinola {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: white;
          box-shadow: 0 2px 4px rgba(231, 76, 60, 0.3);
        }

        .brand-filson {
          background: linear-gradient(135deg, #2c3e50, #34495e);
          color: white;
          box-shadow: 0 2px 4px rgba(44, 62, 80, 0.3);
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group:last-child {
          margin-bottom: 0;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #2c3e50;
          font-weight: 500;
          font-size: 1rem;
        }

        .form-control {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-control:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .radio-group {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          margin-top: 10px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #f8fafc;
          border: 2px solid #dee2e6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 140px;
          position: relative;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .radio-option:hover {
          border-color: #3498db;
          background-color: #e3f2fd;
          transform: translateY(-1px);
        }

        .radio-option:active {
          transform: translateY(0);
        }

        .radio-option input[type="radio"] {
          position: absolute;
          opacity: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          cursor: pointer;
          z-index: 1;
        }

        .radio-option input[type="radio"]:checked + .radio-label {
          color: white;
        }

        .radio-option input[type="radio"]:checked {
          background: #3498db;
          border-color: #3498db;
        }

        .radio-option:has(input[type="radio"]:checked) {
          background: #3498db;
          color: white;
          border-color: #3498db;
        }

        .radio-option input[type="radio"]:focus {
          outline: none;
        }

        .radio-option:has(input[type="radio"]:focus) {
          outline: 2px solid #3498db;
          outline-offset: 2px;
        }

        .radio-label {
          margin: 0;
          cursor: pointer;
          font-weight: 500;
          font-size: 1rem;
          pointer-events: none;
          position: relative;
          z-index: 0;
        }

        .template-summary {
          background: #f8fafc;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          padding: 15px;
          margin: 15px 0;
          font-size: 0.95rem;
        }

        .manual-selection {
          color: #3498db;
          font-weight: 500;
        }

        .auto-selection {
          color: #27ae60;
          font-weight: 500;
        }

        .btn-submit {
          background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
          border: none;
          color: white;
          padding: 16px 32px;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-warning {
          background: linear-gradient(135deg, #f39c12 0%, #e74c3c 100%);
          border: none;
          color: white;
          padding: 12px 24px;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .btn-warning:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 3rem;
          max-width: 550px;
          max-height: 70vh;
          overflow-y: auto;
          width: 90%;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }
        
        .payload-display {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 15px;
          margin: 15px 0;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          white-space: pre-wrap;
          font-size: 13px;
          max-height: 400px;
          overflow-y: auto;
          line-height: 1.4;
        }
        
        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 25px;
        }
        
        .metafields-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #dee2e6;
          margin: 15px 0;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .metafields-table th,
        .metafields-table td {
          border: 1px solid #dee2e6;
          padding: 12px;
          text-align: left;
        }
        
        .metafields-table th {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          font-weight: 600;
          color: #2c3e50;
        }
        
        .metafields-table td.field-name {
          font-weight: 500;
          background-color: #f1f3f4;
        }
        
        .metafields-table td.field-id {
          font-size: 11px;
          color: #666;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        }

        /* Batch Print Modal Styles */
        .batch-modal-content {
          background: white;
          border-radius: 16px;
          padding: 0;
          max-width: 900px;
          max-height: 85vh;
          width: 95%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .batch-modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 25px 30px;
          border-bottom: 3px solid rgba(255, 255, 255, 0.2);
        }

        .batch-modal-header h2 {
          margin: 0 0 20px 0;
          font-size: 1.8rem;
          font-weight: 700;
        }

        .batch-stats {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
          background: rgba(255, 255, 255, 0.15);
          padding: 12px 18px;
          border-radius: 10px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .stat-label {
          font-size: 0.75rem;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.8rem;
          font-weight: 700;
          line-height: 1;
        }

        .stat-success {
          color: #4ade80;
        }

        .stat-error {
          color: #f87171;
        }

        .stat-skipped {
          color: #fbbf24;
        }

        .stat-cancelled {
          color: #9ca3af;
        }

        .staging-warning-banner {
          background: linear-gradient(135deg, #fff3cd 0%, #ffe5a0 100%);
          border: 2px solid #ffc107;
          border-radius: 12px;
          padding: 20px;
          margin: 20px 30px;
          display: flex;
          gap: 15px;
          align-items: flex-start;
          box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
        }

        .warning-icon {
          font-size: 2rem;
          line-height: 1;
        }

        .warning-content {
          flex: 1;
        }

        .warning-content h4 {
          margin: 0 0 12px 0;
          color: #856404;
          font-size: 1.1rem;
        }

        .warning-content p {
          margin: 0 0 8px 0;
          color: #856404;
          font-size: 0.95rem;
        }

        .warning-action {
          font-weight: 600;
          margin-top: 12px !important;
        }

        .batch-mode-selector {
          padding: 20px 30px;
          border-bottom: 1px solid #e1e5e9;
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .mode-label {
          font-weight: 600;
          color: #2c3e50;
          font-size: 1rem;
        }

        .mode-options {
          display: flex;
          gap: 12px;
        }

        .mode-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: 2px solid #dee2e6;
          background: white;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          font-weight: 600;
        }

        .mode-btn:hover:not(:disabled) {
          border-color: #667eea;
          background: #f0f4ff;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(102, 126, 234, 0.2);
        }

        .mode-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #667eea;
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .mode-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .mode-icon {
          font-size: 1.2rem;
          line-height: 1;
        }

        .batch-jobs-container {
          flex: 1;
          overflow-y: auto;
          padding: 20px 30px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .empty-jobs {
          padding: 60px 20px;
          text-align: center;
          color: #999;
          font-size: 1.1rem;
        }

        .job-card {
          background: white;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          padding: 12px 16px;
          transition: all 0.3s ease;
        }

        .job-card.current-job {
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(102, 126, 234, 0.3);
          transform: scale(1.02);
          background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
        }

        .job-card.status-success {
          border-color: #4ade80;
          background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
        }

        .job-card.status-error {
          border-color: #f87171;
          background: linear-gradient(135deg, #fef2f2 0%, #ffffff 100%);
        }

        .job-card.status-skipped {
          border-color: #fbbf24;
          background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
          opacity: 0.7;
        }

        .job-card.status-cancelled {
          border-color: #9ca3af;
          background: linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%);
          opacity: 0.6;
        }

        .job-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 2px solid #f1f3f5;
        }

        .job-number {
          font-size: 1.2rem;
          font-weight: 700;
          color: #667eea;
          background: linear-gradient(135deg, #e7eafe 0%, #f0f4ff 100%);
          padding: 6px 14px;
          border-radius: 8px;
        }

        .job-status-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          text-transform: capitalize;
        }

        .status-icon {
          font-size: 1.2rem;
          line-height: 1;
        }

        .pending-icon {
          animation: pulse-soft 2s infinite;
        }

        .printing-icon {
          animation: spin 1s linear infinite;
        }

        @keyframes pulse-soft {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .job-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 8px;
        }

        .job-detail-row {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .detail-label {
          font-size: 0.7rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }

        .detail-value {
          font-size: 0.9rem;
          color: #1f2937;
          font-weight: 500;
        }

        .detail-template {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.85rem;
          color: #667eea;
        }

        .detail-qty {
          color: #f59e0b;
          font-weight: 600;
        }

        .detail-printer {
          font-size: 0.85rem;
          color: #059669;
        }

        .job-error-message {
          margin-top: 12px;
          padding: 12px;
          background: #fef2f2;
          border-left: 4px solid #f87171;
          border-radius: 6px;
          font-size: 0.85rem;
          color: #991b1b;
        }

        .batch-modal-actions {
          padding: 20px 30px;
          border-top: 2px solid #e1e5e9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
          background: #f8fafc;
          flex-wrap: wrap;
        }

        .manual-controls {
          display: flex;
          gap: 12px;
        }

        .batch-btn {
          padding: 14px 28px;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .batch-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .batch-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .batch-btn-primary {
          background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(46, 204, 113, 0.3);
        }

        .batch-btn-secondary {
          background: #e1e5e9;
          color: #2c3e50;
          border: 2px solid #cbd5e0;
        }

        .batch-btn-secondary:hover:not(:disabled) {
          background: #cbd5e0;
        }

        .batch-btn-skip {
          background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
        }

        .batch-btn-cancel {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          animation: slideInFromRight 0.3s ease;
        }

        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .action-buttons-right {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .progress-bar-container {
          padding: 15px 30px;
          border-bottom: 1px solid #e1e5e9;
          background: #f8fafc;
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          color: #4b5563;
        }

        .progress-bar {
          width: 100%;
          height: 10px;
          background: #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #27ae60 0%, #2ecc71 100%);
          border-radius: 10px;
          transition: width 0.4s ease;
          box-shadow: 0 2px 4px rgba(46, 204, 113, 0.4);
        }

        .completion-summary {
          margin: 20px 30px;
          padding: 25px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border: 2px solid #4ade80;
          border-radius: 12px;
          display: flex;
          gap: 20px;
          align-items: center;
          box-shadow: 0 4px 12px rgba(74, 222, 128, 0.2);
          animation: slideIn 0.5s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .summary-icon {
          font-size: 3rem;
          line-height: 1;
        }

        .summary-text h3 {
          margin: 0 0 8px 0;
          color: #166534;
          font-size: 1.3rem;
        }

        .summary-text p {
          margin: 0;
          color: #15803d;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .App {
            padding: 10px;
          }

          .App-header {
            padding: 15px;
            margin-bottom: 20px;
          }

          .header-content {
            flex-direction: column;
            gap: 15px;
            text-align: center;
            align-items: center;
          }

          .title-section {
            align-items: center;
            text-align: center;
          }

          .App-header h1 {
            font-size: 1.8rem;
            margin: 0;
            line-height: 1.2;
          }

          .staging-badge {
            font-size: 0.75rem;
            padding: 5px 12px;
            gap: 6px;
          }

          .user-info {
            flex-direction: column;
            gap: 10px;
            width: 100%;
          }

          .welcome-text {
            font-size: 1rem;
            word-wrap: break-word;
            max-width: 100%;
          }

          .logout-button {
            padding: 10px 20px;
            font-size: 0.9rem;
            width: auto;
            min-width: 100px;
          }

          .product-details {
            grid-template-columns: 1fr;
          }

          .radio-group {
            flex-direction: column;
            gap: 12px;
          }

          .radio-option {
            min-width: auto;
            justify-content: flex-start;
            padding: 16px 20px;
            min-height: 48px;
          }

          .radio-option input[type="radio"] {
            width: 100%;
            height: 100%;
            min-height: 48px;
          }

          .radio-label {
            font-size: 1.1rem;
          }

          .batch-modal-content {
            width: 100%;
            max-height: 95vh;
            border-radius: 12px;
          }

          .batch-modal-header {
            padding: 20px;
          }

          .batch-modal-header h2 {
            font-size: 1.5rem;
            margin-bottom: 15px;
          }

          .batch-stats {
            gap: 12px;
          }

          .stat-item {
            flex: 1;
            min-width: calc(50% - 6px);
            padding: 10px 12px;
          }

          .stat-label {
            font-size: 0.7rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .staging-warning-banner {
            margin: 15px 20px;
            padding: 15px;
            flex-direction: column;
            gap: 10px;
          }

          .batch-mode-selector {
            padding: 15px 20px;
          }

          .mode-options {
            width: 100%;
            flex-direction: column;
          }

          .mode-btn {
            width: 100%;
            justify-content: center;
            padding: 14px 20px;
          }

          .batch-jobs-container {
            padding: 15px 20px;
            gap: 10px;
          }

          .job-card {
            padding: 12px;
          }

          .job-details {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .batch-modal-actions {
            padding: 15px 20px;
            flex-direction: column;
            gap: 12px;
          }

          .action-buttons-right {
            width: 100%;
            flex-direction: column;
          }

          .manual-controls {
            width: 100%;
            flex-direction: column;
          }

          .batch-btn {
            width: 100%;
            justify-content: center;
            padding: 16px 24px;
            font-size: 1.05rem;
          }

          .progress-bar-container {
            padding: 12px 20px;
          }

          .completion-summary {
            margin: 15px 20px;
            padding: 20px;
            flex-direction: column;
            text-align: center;
          }

          .summary-icon {
            font-size: 2.5rem;
          }

          .summary-text h3 {
            font-size: 1.1rem;
          }

          .summary-text p {
            font-size: 0.9rem;
          }
        }

        @media (max-width: 480px) {
          .App-header h1 {
            font-size: 1.5rem;
          }

          .staging-badge {
            font-size: 0.7rem;
            padding: 4px 10px;
            gap: 4px;
            letter-spacing: 0.5px;
          }

          .staging-icon {
            font-size: 0.9rem;
          }

          .welcome-text {
            font-size: 0.9rem;
          }

          .logout-button {
            padding: 8px 16px;
            font-size: 0.85rem;
          }
        }
      `}</style>

      <header className="App-header">
        <div className="header-content">
          <div className="title-section">
            <h1>Mixo Printer</h1>
            {!window.location.href.includes('mixo.bedrockmfg') && (
              <div className="staging-badge">
                <span className="staging-icon">🚧</span>
                <span className="staging-text">STAGING ENV</span>
                <span className="staging-icon">🚧</span>
              </div>
            )}
          </div>
          <div className="user-info">
            <span className="welcome-text">Welcome, {userName}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>
      <main>
        <form onSubmit={handleSubmit} className="label-form">
          {/* Mode Selection */}
          <div className="form-section">
            <h3>Step 1: Select Mode</h3>
            <ModeSelector
              selectedMode={mode}
              onModeChange={handleModeChange}
            />
          </div>

          {/* UPC Mode */}
          {mode === 'upc' && (
            <>
              <div className="form-section">
                <h3>Step 2: Product Selection</h3>
                <UpcSelect
                  onProductDataFetched={handleProductDataFetched}
                  netSuiteToken={cognitoToken}
                />
              </div>

              {/* Only show additional options if product data is loaded */}
              {productData && (
                <>
                  {/* Product Information with Brand Highlighting */}
                  <div className="form-section product-info">
                    <h3>
                      Product Information
                      {(() => {
                        const { brand, categoryName } = getBrandInfo();
                        if (brand) {
                          return (
                            <span className={`brand-highlight brand-${brand.toLowerCase()}`}>
                              {brand} {categoryName && `- ${categoryName}`}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </h3>
                    <div className="product-details">
                      <p><strong>SKU:</strong> {productData.sku}</p>
                      <p><strong>UPC:</strong> {productData.upc}</p>
                      <p><strong>Name:</strong> {productData.name}</p>
                      <p><strong>Price:</strong> ${productData.retailPrice}</p>
                      <p><strong>Size:</strong> {productData.product_size}</p>
                      <p><strong>Color:</strong> {productData.product_color}</p>
                    </div>
                  </div>

                  {/* Label Configuration */}
                  <div className="form-section">
                    <h3>Step 3: Label Configuration</h3>

                    {/* Quantity */}
                    <div className="form-group">
                      <QtyInput
                        quantity={quantity}
                        onQuantityChange={handleQuantityChange}
                      />
                    </div>

                    {/* Orientation Selection - Only show for Filson */}
                    {shouldShowOrientation(productData.sku) && (
                      <div className="form-group">
                        <label htmlFor="orientation-select"><strong>Orientation</strong></label>
                        <div className="radio-group">
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="orientation-0"
                              name="orientation"
                              value="0"
                              checked={orientation === '0'}
                              onChange={(e) => setOrientation(e.target.value)}
                            />
                            <span className="radio-label">0° (Standard)</span>
                          </div>
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="orientation-90"
                              name="orientation"
                              value="90"
                              checked={orientation === '90'}
                              onChange={(e) => setOrientation(e.target.value)}
                            />
                            <span className="radio-label">90° (Rotated)</span>
                          </div>
                          <div className="radio-option">
                            <input
                              type="radio"
                              id="orientation-180"
                              name="orientation"
                              value="180"
                              checked={orientation === '180'}
                              onChange={(e) => setOrientation(e.target.value)}
                            />
                            <span className="radio-label">180° (Upside Down)</span>
                          </div>
                        </div>
                      </div>
                    )}


                  </div>

                  {/* Template Selection */}
                  <div className="form-section">
                    <h3>Step 4: Template & Printer Selection</h3>

                    {(() => {
                      const { brand } = getBrandInfo();

                      if (brand === 'Shinola') {
                        return (
                          <TemplateSelectShinola
                            key={`${productData.sku}-${lastProductUpdate}`}
                            sku={productData.sku}
                            selectedTemplate={selectedShinolaTemplate}
                            onTemplateChange={setSelectedShinolaTemplate}
                            isNoPrice={productData.priceType === 'none'}
                          />
                        );
                      } else if (brand === 'Filson') {
                        return (
                          <div className="template-summary">
                            <strong>Selected Template: </strong>
                            <span className="auto-selection">
                              {getFilsonTemplateByOrientation(orientation).split('/').pop()}
                              {` (${orientation === '90' ? '90° Rotated' : orientation === '180' ? '180° Upside Down' : 'Standard'})`}
                            </span>
                          </div>
                        );
                      } else {
                        return (
                          <div className="template-summary">
                            <p style={{ color: '#e74c3c', fontStyle: 'italic' }}>
                              No templates available for this SKU pattern. Please check the SKU format.
                            </p>
                          </div>
                        );
                      }
                    })()}

                    <PrinterSelect
                      selectedPrinter={selectedPrinter}
                      onPrinterChange={handlePrinterChange}
                      authToken={cognitoToken}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="form-section">
                    <button type="submit" className="btn btn-success btn-submit" disabled={!isFormValid() || isLoading}>
                      {isLoading ? 'Printing...' : `🖨️ Print Labels (${quantity} ${quantity === 1 ? 'copy' : 'copies'})`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* TO/SO Mode */}
          {mode === 'toso' && (
            <>
              <div className="form-section">
                <h3>Step 2: Enter TO/SO ID</h3>
                <ToSoInput
                  onDataFetched={handleToSoDataFetched}
                  authToken={cognitoToken}
                />
              </div>

              {tosoProducts.length > 0 && (
                <>
                  <div className="form-section">
                    <h3>Step 3: Configure Products & Printers</h3>
                    {/* Global printer for TO/SO */}
                    <div className="form-group">
                      <label><strong>Global Printer (used when line printer not selected)</strong></label>
                      <PrinterSelect
                        selectedPrinter={selectedPrinter}
                        onPrinterChange={handlePrinterChange}
                        authToken={cognitoToken}
                      />
                    </div>
                    {/* Pagination Navigation Banner */}
                    {paginationInfo.totalPages && paginationInfo.totalPages > 1 && (
                      <div style={{
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                        border: '2px solid #667eea',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2c3e50' }}>
                                📄 Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                Showing {tosoProducts.length} unique {tosoProducts.length === 1 ? 'item' : 'items'} on this page
                              </div>
                              {/* <div style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>
                                ({paginationInfo.totalResults} raw records total in NetSuite)
                              </div> */}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#667eea', fontWeight: '600', background: 'white', padding: '8px 16px', borderRadius: '20px', border: '1px solid #667eea' }}>
                              💡 Navigate pages without printing
                            </div>
                          </div>

                          {/* Page Navigation Controls */}
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => handleNavigateToPage(1)}
                              disabled={isLoadingMorePages || paginationInfo.currentPage === 1}
                              style={{
                                background: paginationInfo.currentPage === 1 ? '#e1e5e9' : 'white',
                                color: paginationInfo.currentPage === 1 ? '#999' : '#667eea',
                                border: '2px solid #667eea',
                                borderRadius: '8px',
                                padding: '10px 16px',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                cursor: paginationInfo.currentPage === 1 || isLoadingMorePages ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                opacity: paginationInfo.currentPage === 1 ? 0.5 : 1
                              }}
                            >
                              ⏮️ First
                            </button>

                            <button
                              type="button"
                              onClick={() => handleNavigateToPage(paginationInfo.currentPage - 1)}
                              disabled={isLoadingMorePages || paginationInfo.currentPage === 1}
                              style={{
                                background: paginationInfo.currentPage === 1 ? '#e1e5e9' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                cursor: paginationInfo.currentPage === 1 || isLoadingMorePages ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: paginationInfo.currentPage === 1 ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)',
                                opacity: paginationInfo.currentPage === 1 ? 0.5 : 1
                              }}
                            >
                              ◀️ Previous
                            </button>

                            <div style={{
                              background: 'white',
                              border: '2px solid #667eea',
                              borderRadius: '8px',
                              padding: '10px 20px',
                              fontWeight: '700',
                              fontSize: '1rem',
                              color: '#667eea',
                              minWidth: '100px',
                              textAlign: 'center'
                            }}>
                              {isLoadingMorePages ? '⏳...' : `${paginationInfo.currentPage} / ${paginationInfo.totalPages}`}
                            </div>

                            <button
                              type="button"
                              onClick={() => handleNavigateToPage(paginationInfo.currentPage + 1)}
                              disabled={isLoadingMorePages || paginationInfo.currentPage === paginationInfo.totalPages}
                              style={{
                                background: paginationInfo.currentPage === paginationInfo.totalPages ? '#e1e5e9' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                cursor: paginationInfo.currentPage === paginationInfo.totalPages || isLoadingMorePages ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: paginationInfo.currentPage === paginationInfo.totalPages ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)',
                                opacity: paginationInfo.currentPage === paginationInfo.totalPages ? 0.5 : 1
                              }}
                            >
                              Next ▶️
                            </button>

                            <button
                              type="button"
                              onClick={() => handleNavigateToPage(paginationInfo.totalPages)}
                              disabled={isLoadingMorePages || paginationInfo.currentPage === paginationInfo.totalPages}
                              style={{
                                background: paginationInfo.currentPage === paginationInfo.totalPages ? '#e1e5e9' : 'white',
                                color: paginationInfo.currentPage === paginationInfo.totalPages ? '#999' : '#667eea',
                                border: '2px solid #667eea',
                                borderRadius: '8px',
                                padding: '10px 16px',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                cursor: paginationInfo.currentPage === paginationInfo.totalPages || isLoadingMorePages ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                opacity: paginationInfo.currentPage === paginationInfo.totalPages ? 0.5 : 1
                              }}
                            >
                              Last ⏭️
                            </button>
                          </div>
                        </div>
                      </div>
                    )}



                    {/* Default Price Settings */}
                    <div className="default-price-settings">
                      <style jsx>{`
                      .default-price-settings {
                        background: linear-gradient(135deg, #f8fafc 0%, #e8f4f8 100%);
                        border: 2px solid #667eea;
                        border-radius: 12px;
                        padding: 20px;
                        margin-top: 20px;
                        margin-bottom: 20px;
                        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
                      }

                      .default-price-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-bottom: 20px;
                        flex-wrap: wrap;
                        gap: 10px;
                      }

                      .default-price-title {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                      }

                      .apply-button {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        border: none;
                        border-radius: 8px;
                        padding: 10px 20px;
                        font-weight: 600;
                        font-size: 0.95rem;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
                        white-space: nowrap;
                      }

                      .apply-button:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                      }

                      .apply-button:not(:disabled):hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
                      }

                      .price-controls-grid {
                        display: flex;
                        gap: 20px;
                        margin-bottom: 15px;
                        align-items: stretch;
                      }

                      .price-divider {
                        width: 2px;
                        background: linear-gradient(to bottom, transparent, rgba(102, 126, 234, 0.3) 20%, rgba(102, 126, 234, 0.3) 80%, transparent);
                        margin: 0 10px;
                        flex-shrink: 0;
                      }

                      .price-control-section {
                        display: flex;
                        flex-direction: column;
                        flex: 1;
                        min-width: 250px;
                      }

                      .control-label {
                        display: block;
                        font-size: 0.85rem;
                        font-weight: 700;
                        color: #666;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        margin-bottom: 10px;
                      }

                      .price-button-group {
                        display: flex;
                        gap: 10px;
                      }

                      .price-type-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 8px;
                      }

                      .price-button {
                        flex: 1;
                        padding: 12px;
                        border: 2px solid #dee2e6;
                        border-radius: 8px;
                        font-weight: 600;
                        font-size: 0.95rem;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        background: white;
                        color: #2c3e50;
                      }

                      .price-button.selected {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        border-color: #667eea;
                      }

                      .price-button:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                      }

                      .price-button:not(:disabled):hover {
                        border-color: #667eea;
                        background: #e3f2fd;
                      }

                      .price-button.selected:hover {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                      }

                      .price-type-button {
                        padding: 12px 10px;
                        border: 2px solid #dee2e6;
                        border-radius: 8px;
                        font-weight: 600;
                        font-size: 0.85rem;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        background: white;
                        color: #2c3e50;
                      }

                      .price-type-button.selected {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        border-color: #667eea;
                      }

                      .info-box {
                        background: rgba(255, 255, 255, 0.7);
                        border-radius: 8px;
                        padding: 12px 15px;
                        font-size: 0.9rem;
                        color: #555;
                        display: flex;
                        align-items: flex-start;
                        gap: 10px;
                      }

                      @media (max-width: 768px) {
                        .default-price-settings {
                          padding: 15px;
                        }

                        .default-price-header {
                          flex-direction: column;
                          align-items: stretch;
                        }

                        .apply-button {
                          width: 100%;
                          padding: 14px 20px;
                          font-size: 1rem;
                        }

                        .price-controls-grid {
                          flex-direction: column;
                          gap: 20px;
                        }

                        .price-divider {
                          display: none;
                        }

                        .price-button-group {
                          flex-direction: row;
                        }

                        .price-type-grid {
                          grid-template-columns: repeat(2, 1fr);
                        }

                        .price-control-section {
                          min-width: auto;
                        }
                      }
                    `}</style>
                      <div className="default-price-header">
                        <div className="default-price-title">
                          <span style={{ fontSize: '1.5rem' }}>💰</span>
                          <h4 style={{ margin: 0, color: '#2c3e50', fontSize: '1.1rem', fontWeight: '700' }}>
                            Default Pricing for All Items
                          </h4>
                        </div>
                        <button
                          type="button"
                          onClick={applyDefaultPriceToAll}
                          disabled={tosoProducts.length === 0}
                          className="apply-button"
                        >
                          ⚡ Apply to All Items
                        </button>
                      </div>

                      <div className="price-controls-grid">
                        {/* Currency Selection */}
                        <div className="price-control-section">
                          <label className="control-label">
                            Region / Currency
                            {(defaultPriceSettings.selectedPriceType === 'line' || defaultPriceSettings.selectedPriceType === 'none') && (
                              <span style={{ fontSize: '0.75rem', color: '#999', fontWeight: 'normal', marginLeft: '8px' }}>
                                (N/A for {defaultPriceSettings.selectedPriceType === 'line' ? 'line price' : 'no price'})
                              </span>
                            )}
                          </label>
                          <div className="price-button-group">
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedCurrency: 'USD' })}
                              disabled={defaultPriceSettings.selectedPriceType === 'line' || defaultPriceSettings.selectedPriceType === 'none'}
                              className={`price-button ${defaultPriceSettings.selectedCurrency === 'USD' ? 'selected' : ''}`}
                            >
                              🇺🇸 USD
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedCurrency: 'CAD' })}
                              disabled={defaultPriceSettings.selectedPriceType === 'line' || defaultPriceSettings.selectedPriceType === 'none'}
                              className={`price-button ${defaultPriceSettings.selectedCurrency === 'CAD' ? 'selected' : ''}`}
                            >
                              🇨🇦 CAD
                            </button>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="price-divider"></div>

                        {/* Price Type Selection */}
                        <div className="price-control-section">
                          <label className="control-label">
                            Price Type
                          </label>
                          <div className="price-type-grid">
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedPriceType: 'current' })}
                              className={`price-type-button ${defaultPriceSettings.selectedPriceType === 'current' ? 'selected' : ''}`}
                            >
                              Current
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedPriceType: 'future' })}
                              className={`price-type-button ${defaultPriceSettings.selectedPriceType === 'future' ? 'selected' : ''}`}
                            >
                              Future
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedPriceType: 'outlet' })}
                              className={`price-type-button ${defaultPriceSettings.selectedPriceType === 'outlet' ? 'selected' : ''}`}
                            >
                              Outlet
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedPriceType: 'line' })}
                              className={`price-type-button ${defaultPriceSettings.selectedPriceType === 'line' ? 'selected' : ''}`}
                            >
                              📋 Line
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDefaultPriceChange({ selectedPriceType: 'none' })}
                              className={`price-type-button ${defaultPriceSettings.selectedPriceType === 'none' ? 'selected' : ''}`}
                            >
                              🚫 None
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Info text */}
                      <div className="info-box">
                        <span style={{ fontSize: '1rem', flexShrink: 0 }}>💡</span>
                        <div>
                          <strong>Tip:</strong> Set your preferred pricing defaults here, then click "Apply to All Items" to update all products at once.
                          Individual items can still be customized by expanding them below.
                        </div>
                      </div>
                    </div>

                    <ProductTable
                      products={tosoProducts}
                      productStates={tosoProductStates}
                      onProductsChange={handleToSoProductsChange}
                      allPrinters={allPrinters}
                      tranid={tranid}
                      transactionType={transactionType}
                      onPrintLine={handlePrintLine}
                      defaultPriceSettings={defaultPriceSettings}
                    />
                  </div>


                  {/* Submit Button */}
                  <div className="form-section">
                    {paginationInfo.totalPages && paginationInfo.totalPages > 1 && (
                      <div style={{
                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                        border: '2px solid #ff9800',
                        borderRadius: '10px',
                        padding: '15px',
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#e65100', marginBottom: '4px' }}>
                            Note: You're on page {paginationInfo.currentPage} of {paginationInfo.totalPages}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#f57c00' }}>
                            This will only print items from the current page. Navigate to other pages to print those items.
                          </div>
                        </div>
                      </div>
                    )}
                    <button type="button" onClick={openBatchModal} className="btn btn-success btn-submit" disabled={!isFormValid() || isLoading}>
                      {isLoading ? 'Printing...' : `🖨️ Print Labels on This Page (${Object.values(tosoProductStates).reduce((sum, state) => sum + (state?.enabled ? (state?.quantity || 0) : 0), 0)} total)`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </form>
      </main>

      {/* Quantity Confirmation Modal */}
      {showQuantityConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 style={{ color: '#e74c3c', borderBottom: '2px solid #e74c3c', paddingBottom: '10px' }}>
              ⚠️ High Quantity Warning
            </h3>
            <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', padding: '15px', margin: '15px 0' }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '1.1rem' }}>
                You are about to print <strong style={{ color: '#e74c3c' }}>{pendingQuantity} labels</strong>.
              </p>
              <p style={{ margin: '0', color: '#856404' }}>
                This is a large quantity that may consume significant printer resources
                and materials. Are you sure you want to proceed?
              </p>
            </div>

            <div className="modal-actions">
              <button
                onClick={handleCancelHighQuantity}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmHighQuantity}
                className="btn btn-warning"
              >
                Yes, Print {pendingQuantity} Labels
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metafields Modal */}
      {showMetafieldsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Template Metafields</h3>
            {isLoadingMetafields ? (
              <p>Loading metafields...</p>
            ) : templateMetafields?.error ? (
              <div className="payload-display">
                <p style={{ color: 'red' }}>{templateMetafields.error}</p>
              </div>
            ) : (
              <div>
                <h4>{templateMetafields?.fileName}</h4>

                {/* Named Data Sources Table */}
                {templateMetafields?.namedDataSources && templateMetafields.namedDataSources.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h5>Named Data Sources:</h5>
                    <table className="metafields-table">
                      <thead>
                        <tr>
                          <th className="field-name">Name</th>
                          <th className="field-name">Current Value</th>
                          <th className="field-id">ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templateMetafields.namedDataSources.map((source, index) => (
                          <tr key={source.Id || index}>
                            <td className="field-name">{source.Name}</td>
                            <td>{source.Value || '(empty)'}</td>
                            <td className="field-id">{source.Id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Full Metadata (Collapsible) */}
                <details style={{ marginTop: '20px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                    Show Full Metadata
                  </summary>
                  <div className="payload-display">
                    {JSON.stringify(templateMetafields?.fullMetadata, null, 2)}
                  </div>
                </details>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleCloseMetafieldsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && printResult && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 style={{
              color: printResult.success ? '#27ae60' : '#e74c3c',
              borderBottom: printResult.success ? '2px solid #27ae60' : '2px solid #e74c3c',
              paddingBottom: '10px'
            }}>
              {printResult.success ? '✅ Print Job Successful' : '❌ Print Job Failed'}
            </h3>

            <div style={{
              backgroundColor: printResult.success ? '#d4edda' : '#f8d7da',
              border: printResult.success ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
              borderRadius: '6px',
              padding: '15px',
              margin: '15px 0'
            }}>
              <p style={{ margin: '0', fontSize: '1.1rem' }}>
                {printResult.message}
              </p>
            </div>

            {/* Show details in a collapsible section for debugging */}
            <details style={{ marginTop: '20px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                Show Technical Details
              </summary>
              <div className="payload-display">
                {JSON.stringify(printResult.details, null, 2)}
              </div>
            </details>

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleCloseResultModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TO/SO Batch Print Modal */}
      {showBatchModal && (
        <div className="modal-overlay">
          <div className="batch-modal-content">
            <div className="batch-modal-header">
              <h2>🖨️ Batch Print Manager</h2>
              <div className="batch-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Jobs</span>
                  <span className="stat-value">{batchJobs.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Completed</span>
                  <span className="stat-value stat-success">{batchJobs.filter(j => j.status === 'success').length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Failed</span>
                  <span className="stat-value stat-error">{batchJobs.filter(j => j.status === 'error').length}</span>
                </div>
                {batchPrintMode === 'manual' && (
                  <div className="stat-item">
                    <span className="stat-label">Skipped</span>
                    <span className="stat-value stat-skipped">{batchJobs.filter(j => j.status === 'skipped').length}</span>
                  </div>
                )}
                {batchJobs.some(j => j.status === 'cancelled') && (
                  <div className="stat-item">
                    <span className="stat-label">Cancelled</span>
                    <span className="stat-value stat-cancelled">{batchJobs.filter(j => j.status === 'cancelled').length}</span>
                  </div>
                )}
              </div>
            </div>

            {isStaging && batchJobs.length > STAGING_PRINT_LIMIT && (
              <div className="staging-warning-banner">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <h4>Staging Environment Limitation</h4>
                  <p>You have {batchJobs.length} print jobs, but staging is limited to {STAGING_PRINT_LIMIT} commands per run.</p>
                  <p className="warning-action">Please reduce the number of items or use the production environment.</p>
                </div>
              </div>
            )}

            <div className="batch-mode-selector">
              <label className="mode-label">Print Mode:</label>
              <div className="mode-options">
                <button
                  className={`mode-btn ${batchPrintMode === 'auto' ? 'active' : ''}`}
                  onClick={() => setBatchPrintMode('auto')}
                  disabled={isBatchPrinting}
                >
                  <span className="mode-icon">⚡</span>
                  <span className="mode-text">Auto</span>
                </button>
                <button
                  className={`mode-btn ${batchPrintMode === 'manual' ? 'active' : ''}`}
                  onClick={() => setBatchPrintMode('manual')}
                  disabled={isBatchPrinting}
                >
                  <span className="mode-icon">👆</span>
                  <span className="mode-text">Step-by-Step</span>
                </button>
              </div>
            </div>

            {isBatchPrinting && batchPrintMode === 'auto' && (
              <div className="progress-bar-container">
                <div className="progress-info">
                  <span>Processing jobs...</span>
                  <span>{batchJobs.filter(j => j.status === 'success' || j.status === 'error').length} / {batchJobs.length}</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${(batchJobs.filter(j => j.status === 'success' || j.status === 'error').length / batchJobs.length) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}

            {batchJobs.length === 0 ? (
              <div className="empty-jobs">
                <p>No jobs prepared.</p>
              </div>
            ) : (
              <>
                {/* Completion Summary */}
                {batchJobs.every(j => j.status !== 'pending' && j.status !== 'printing') && (
                  <div className="completion-summary">
                    <div className="summary-icon">
                      {batchJobs.filter(j => j.status === 'success').length === batchJobs.length ? '🎉' :
                        batchJobs.filter(j => j.status === 'error').length === 0 ? '✅' : '⚠️'}
                    </div>
                    <div className="summary-text">
                      {batchJobs.filter(j => j.status === 'success').length === batchJobs.length ? (
                        <h3>All Jobs Completed Successfully! 🎉</h3>
                      ) : (
                        <h3>Batch Processing Complete</h3>
                      )}
                      <p>
                        {batchJobs.filter(j => j.status === 'success').length} successful
                        {batchJobs.filter(j => j.status === 'error').length > 0 && `, ${batchJobs.filter(j => j.status === 'error').length} failed`}
                        {batchJobs.filter(j => j.status === 'skipped').length > 0 && `, ${batchJobs.filter(j => j.status === 'skipped').length} skipped`}
                        {batchJobs.filter(j => j.status === 'cancelled').length > 0 && `, ${batchJobs.filter(j => j.status === 'cancelled').length} cancelled`}
                      </p>
                    </div>
                  </div>
                )}
                <div className="batch-jobs-container">
                  {batchJobs.map((job, i) => {
                    const isCurrentJob = batchPrintMode === 'manual' && i === currentBatchIndex;
                    const statusClass = `job-card status-${job.status} ${isCurrentJob ? 'current-job' : ''}`;

                    return (
                      <div key={`${job.index}-${i}`} className={statusClass}>
                        <div className="job-header">
                          <div className="job-number">#{job.index + 1}</div>
                          <div className="job-status-badge">
                            {job.status === 'pending' && <span className="status-icon pending-icon">⏳</span>}
                            {job.status === 'printing' && <span className="status-icon printing-icon">🔄</span>}
                            {job.status === 'success' && <span className="status-icon success-icon">✅</span>}
                            {job.status === 'error' && <span className="status-icon error-icon">❌</span>}
                            {job.status === 'skipped' && <span className="status-icon skipped-icon">⏭️</span>}
                            {job.status === 'cancelled' && <span className="status-icon cancelled-icon">🚫</span>}
                            <span className="status-text">{job.status}</span>
                          </div>
                        </div>

                        <div className="job-details">
                          <div className="job-detail-row">
                            <span className="detail-label">SKU:</span>
                            <span className="detail-value">{job.product.itemid}</span>
                          </div>
                          <div className="job-detail-row">
                            <span className="detail-label">Template:</span>
                            <span className="detail-value detail-template">{job.template?.split('/').pop()}</span>
                          </div>
                          <div className="job-detail-row">
                            <span className="detail-label">Quantity:</span>
                            <span className="detail-value detail-qty">{job.quantity} {job.quantity === 1 ? 'copy' : 'copies'}</span>
                          </div>
                          <div className="job-detail-row">
                            <span className="detail-label">Printer:</span>
                            <span className="detail-value detail-printer">{getPrinterLabel(job.printerId)}</span>
                          </div>
                        </div>

                        {job.status === 'error' && job.response && (
                          <div className="job-error-message">
                            <strong>Error:</strong> {typeof job.response === 'string' ? job.response : JSON.stringify(job.response)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="batch-modal-actions">
              {batchPrintMode === 'auto' ? (
                <>
                  <button
                    className="batch-btn batch-btn-secondary"
                    onClick={closeBatchModal}
                    disabled={isBatchPrinting}
                  >
                    {isBatchPrinting ? 'Printing...' : 'Close'}
                  </button>
                  <div className="action-buttons-right">
                    {isBatchPrinting && (
                      <button
                        className="batch-btn batch-btn-cancel"
                        onClick={handleCancelBatchPrint}
                        disabled={cancelBatchPrint}
                      >
                        {cancelBatchPrint ? (
                          <>
                            <span className="btn-spinner"></span>
                            Cancelling...
                          </>
                        ) : (
                          <>🛑 Cancel</>
                        )}
                      </button>
                    )}
                    <button
                      className="batch-btn batch-btn-primary"
                      onClick={startBatchPrinting}
                      disabled={
                        isBatchPrinting ||
                        batchJobs.length === 0 ||
                        (isStaging && (batchJobs.length > STAGING_PRINT_LIMIT || batchJobs.some(job => job.quantity > STAGING_PRINT_LIMIT)))
                      }
                    >
                      {isBatchPrinting ? (
                        <>
                          <span className="btn-spinner"></span>
                          Printing All...
                        </>
                      ) : (
                        <>⚡ Start Auto Print</>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    className="batch-btn batch-btn-secondary"
                    onClick={closeBatchModal}
                    disabled={isBatchPrinting}
                  >
                    Close
                  </button>
                  <div className="manual-controls">
                    <button
                      className="batch-btn batch-btn-skip"
                      onClick={handleManualSkip}
                      disabled={isBatchPrinting || currentBatchIndex >= batchJobs.length || batchJobs[currentBatchIndex]?.status !== 'pending'}
                    >
                      ⏭️ Skip
                    </button>
                    <button
                      className="batch-btn batch-btn-primary"
                      onClick={handleManualNext}
                      disabled={isBatchPrinting || currentBatchIndex >= batchJobs.length || batchJobs[currentBatchIndex]?.status !== 'pending'}
                    >
                      {isBatchPrinting ? (
                        <>
                          <span className="btn-spinner"></span>
                          Printing...
                        </>
                      ) : currentBatchIndex >= batchJobs.length - 1 ? (
                        <>✅ Print Last</>
                      ) : (
                        <>🖨️ Print & Next</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LabelPrinterApp;

